import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  Sparkles,
  Layers,
  Loader2,
  MessageSquare,
  MessageSquarePlus,
  Clock,
  MoreVertical,
  Edit,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateWithoutYear } from '../../utils/dateFormat';

interface Output {
  id: string;
  type: 'summary' | 'quiz' | 'flashcards' | 'outline' | 'custom';
  title: string;
  content: string;
  timestamp: string;
  sources?: string[];
}

interface ChatHistory {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
}

interface ToolboxPanelProps {
  outputs?: Output[];
  loading?: boolean;
  onPresetSelect?: (presetType: string) => void;
  chatHistory?: ChatHistory[];
  chatHistoryLoading?: boolean;
  chatHistoryHasMore?: boolean; // Add: whether more chats exist
  chatHistoryError?: string | null; // Add: error message
  onChatSelect?: (chatId: string) => void;
  onNewChat?: () => void;
  onChatRename?: (chatId: string, oldTitle: string, newTitle: string) => void;
  onChatDelete?: (chatId: string, title: string) => void;
  onLoadMoreChats?: () => void; // Add: callback to load more chats
  knowledgeBaseId?: string; // Add: current KB ID for API calls
}

export const ToolboxPanel: React.FC<ToolboxPanelProps> = ({
  onPresetSelect,
  chatHistory = [],
  chatHistoryLoading = false,
  chatHistoryHasMore = true,
  chatHistoryError = null,
  onChatSelect,
  onNewChat,
  onChatRename,
  onChatDelete,
  onLoadMoreChats,
  knowledgeBaseId: _knowledgeBaseId,
}) => {
  const { t } = useTranslation();
  const [chatActionMenuOpen, setChatActionMenuOpen] = useState<string | null>(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedChat, setSelectedChat] = useState<ChatHistory | null>(null);
  const [newChatTitle, setNewChatTitle] = useState('');
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const SCROLL_THRESHOLD = 100;

  const historyListRef = useRef<HTMLDivElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;

    // Trigger when within threshold of bottom
    const isNearBottom = scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD;

    if (isNearBottom && onLoadMoreChats && chatHistoryHasMore && !chatHistoryLoading) {
      onLoadMoreChats();
    }
  };

  const handleGenerate = (type: string) => {
    if (onPresetSelect) {
      onPresetSelect(type);
    }
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (chatActionMenuOpen && actionMenuRef.current) {
        const target = event.target as Node;
        if (!actionMenuRef.current.contains(target)) {
          setChatActionMenuOpen(null);
        }
      }
    };

    if (chatActionMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [chatActionMenuOpen]);

  const handleRenameClick = (chat: ChatHistory) => {
    setSelectedChat(chat);
    setNewChatTitle(chat.title);
    setShowRenameDialog(true);
    setChatActionMenuOpen(null);
  };

  const handleRenameSubmit = () => {
    if (selectedChat && onChatRename) {
      onChatRename(selectedChat.id, selectedChat.title, newChatTitle);
      setShowRenameDialog(false);
      setSelectedChat(null);
      setNewChatTitle('');
    }
  };

  const handleDeleteClick = (chat: ChatHistory) => {
    setSelectedChat(chat);
    setShowDeleteDialog(true);
    setChatActionMenuOpen(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedChat && onChatDelete) {
      onChatDelete(selectedChat.id, selectedChat.title);
      setShowDeleteDialog(false);
      setSelectedChat(null);
    }
  };

  const tools = [
    { id: 'summary', type: 'summary' as const, icon: FileText, color: 'bg-blue-50 text-blue-600' },
    { id: 'quiz', type: 'quiz' as const, icon: Sparkles, color: 'bg-purple-50 text-purple-600' },
    {
      id: 'flashcards',
      type: 'flashcards' as const,
      icon: Layers,
      color: 'bg-green-50 text-green-600',
    },
    {
      id: 'outline',
      type: 'outline' as const,
      icon: Layers,
      color: 'bg-orange-50 text-orange-600',
    },
  ];

  return (
    <div className='flex flex-col h-full overflow-hidden'>
      {/* Header */}
      <div className='flex-shrink-0 border-b border-gray-200 px-4 py-3'>
        <h2 className='font-semibold text-gray-900 text-sm'>{t('knowledge.toolbox.tools')}</h2>
      </div>

      <div className='flex flex-col flex-1 overflow-hidden'>
        {/* Tools Section */}
        <div className='flex-shrink-0 p-4 border-b border-gray-200'>
          <h3 className='text-xs font-medium text-gray-500 uppercase tracking-wider mb-3'>
            {t('knowledge.toolbox.generate')}
          </h3>
          <div className='space-y-2'>
            {tools.map(tool => (
              <button
                key={tool.id}
                onClick={() => handleGenerate(tool.type)}
                className='w-full flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all text-left'
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 ${tool.color} rounded-lg flex items-center justify-center`}
                >
                  <tool.icon size={16} />
                </div>
                <span className='text-sm font-medium text-gray-900'>
                  {t(`knowledge.toolbox.${tool.type}`)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Chat History Section */}
        <div className='flex flex-col h-full overflow-hidden'>
          <div className='flex-shrink-0 px-4 pt-4'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                {t('knowledge.toolbox.chatHistory', 'Chat History')}
              </h3>
              {onNewChat && (
                <button
                  onClick={onNewChat}
                  className='flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors'
                  title={t('chat.newConversation', 'New Conversation')}
                >
                  <MessageSquarePlus size={14} />
                  <span>{t('chat.newConversation', 'New')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Scrollable chat list */}
          <div
            ref={historyListRef}
            className='flex-1 overflow-y-auto px-4 pb-4'
            onScroll={handleScroll}
          >
            {chatHistoryError ? (
              <div className='flex flex-col items-center justify-center py-8 text-center'>
                <p className='text-xs text-red-500'>{chatHistoryError}</p>
                <button
                  onClick={onLoadMoreChats}
                  className='mt-2 text-xs text-blue-600 hover:underline'
                >
                  {t('common.retry', 'Retry')}
                </button>
              </div>
            ) : chatHistoryLoading && chatHistory.length === 0 ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2 className='text-gray-400 animate-spin' size={24} />
              </div>
            ) : chatHistory.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-8 text-center'>
                <MessageSquare className='text-gray-300' size={32} />
                <p className='text-xs text-gray-500 mt-2'>
                  {t('knowledge.toolbox.noChatHistory', 'No chat history yet')}
                </p>
              </div>
            ) : (
              <div className='space-y-1.5'>
                {chatHistory.map(chat => (
                  <div
                    key={chat.id}
                    className='relative group'
                    ref={chatActionMenuOpen === chat.id ? actionMenuRef : null}
                  >
                    <div
                      className='p-2.5 pr-8 rounded-ui-md text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer transition-all duration-200 text-sm'
                      onClick={() => onChatSelect?.(chat.id)}
                    >
                      <div className='truncate group-hover:text-gray-900 transition-colors font-body'>
                        {chat.title || t('knowledge.toolbox.untitledChat', 'Untitled Chat')}
                      </div>
                      <div className='flex items-center gap-1.5 mt-0.5'>
                        <Clock size={11} className='text-gray-400' />
                        <span className='text-xs text-gray-500'>
                          {formatDateWithoutYear(chat.updated_at)}
                        </span>
                      </div>
                    </div>

                    {/* Action menu button */}
                    <div className='absolute bottom-1 right-1'>
                      <button
                        className={`p-1 hover:bg-gray-200 rounded-lg transition-all duration-200 ${
                          chatActionMenuOpen === chat.id
                            ? 'opacity-100 bg-gray-200'
                            : 'lg:opacity-0 lg:invisible lg:group-hover:opacity-100 lg:group-hover:visible'
                        }`}
                        onClick={e => {
                          e.stopPropagation();
                          setChatActionMenuOpen(chatActionMenuOpen === chat.id ? null : chat.id);
                        }}
                        aria-label={`Actions for ${chat.title}`}
                      >
                        <MoreVertical size={14} className='text-gray-500' />
                      </button>

                      {/* Action Menu Dropdown */}
                      {chatActionMenuOpen === chat.id && (
                        <div
                          className='absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px] z-20'
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                            onClick={() => handleRenameClick(chat)}
                          >
                            <Edit size={14} />
                            {t('chat.renameConversation', 'Rename')}
                          </button>
                          <div className='border-t border-gray-100 my-1' />
                          <button
                            className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-red-600'
                            onClick={() => handleDeleteClick(chat)}
                          >
                            <Trash2 size={14} />
                            {t('chat.deleteConversation', 'Delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {/* Loading more indicator */}
                {chatHistoryLoading && chatHistory.length > 0 && (
                  <div className='flex items-center justify-center py-4'>
                    <Loader2 className='text-gray-400 animate-spin' size={20} />
                  </div>
                )}
                {/* End of list indicator */}
                {!chatHistoryHasMore && chatHistory.length > 0 && (
                  <div className='text-center py-2'>
                    <p className='text-xs text-gray-400'>{t('knowledge.toolbox.allChatsLoaded')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rename Dialog */}
      {showRenameDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 w-full max-w-md mx-4'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              {t('chat.renameConversation', 'Rename Chat')}
            </h3>
            <input
              type='text'
              value={newChatTitle}
              onChange={e => setNewChatTitle(e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder={t('knowledge.toolbox.untitledChat', 'Untitled Chat')}
              autoFocus
            />
            <div className='flex justify-end gap-2 mt-4'>
              <button
                onClick={() => {
                  setShowRenameDialog(false);
                  setSelectedChat(null);
                  setNewChatTitle('');
                }}
                className='px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleRenameSubmit}
                disabled={!newChatTitle.trim()}
                className='px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors'
              >
                {t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 w-full max-w-md mx-4'>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>
              {t('chat.deleteConversation', 'Delete Chat')}
            </h3>
            <p className='text-sm text-gray-600 mb-4'>
              {t(
                'chat.confirmDeleteConversation',
                'Are you sure you want to delete this chat? This action cannot be undone.'
              )}
            </p>
            <div className='flex justify-end gap-2'>
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setSelectedChat(null);
                }}
                className='px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className='px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors'
              >
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
